/**
 * 题目类型注册表
 */

import type { QuestionType } from '../types';
import type { QuestionTypeMetadata, ExamSpecificQuestionTypeDefinition } from './types';

/**
 * 基础类型注册表
 */
const typeRegistry = new Map<QuestionType, QuestionTypeMetadata>();

/**
 * 考试特定类型注册表
 * 按考试ID组织
 */
const examTypeRegistry = new Map<string, Map<string, ExamSpecificQuestionTypeDefinition>>();

/**
 * 注册题目类型（基础类型）
 */
export function registerQuestionType(metadata: QuestionTypeMetadata): void {
  if (typeRegistry.has(metadata.type)) {
    console.warn(`[QuestionTypeRegistry] 类型 ${metadata.type} 已存在，将被覆盖`);
  }
  typeRegistry.set(metadata.type, metadata);
}

/**
 * 获取题目类型元数据（基础类型）
 */
export function getQuestionTypeMetadata(type: QuestionType): QuestionTypeMetadata | undefined {
  return typeRegistry.get(type);
}

/**
 * 获取所有已注册的题目类型
 */
export function getAllQuestionTypes(): QuestionTypeMetadata[] {
  return Array.from(typeRegistry.values());
}

/**
 * 检查类型是否已注册
 */
export function isQuestionTypeRegistered(type: QuestionType): boolean {
  return typeRegistry.has(type);
}

/**
 * 清除所有注册的类型（主要用于测试）
 */
export function clearRegistry(): void {
  typeRegistry.clear();
  examTypeRegistry.clear();
}

// ==================== 考试特定类型注册表 ====================

/**
 * 注册考试特定的题目类型
 */
export function registerExamQuestionType(
  examId: string,
  definition: ExamSpecificQuestionTypeDefinition
): void {
  if (!examTypeRegistry.has(examId)) {
    examTypeRegistry.set(examId, new Map());
  }

  const examRegistry = examTypeRegistry.get(examId)!;

  if (examRegistry.has(definition.id)) {
    console.warn(
      `[ExamQuestionTypeRegistry] 考试 ${examId} 的类型 ${definition.id} 已存在，将被覆盖`
    );
  }

  examRegistry.set(definition.id, definition);
}

/**
 * 批量注册考试特定的题目类型
 */
export function registerExamQuestionTypes(
  examId: string,
  definitions: ExamSpecificQuestionTypeDefinition[]
): void {
  for (const definition of definitions) {
    registerExamQuestionType(examId, definition);
  }
}

/**
 * 获取考试特定的题目类型定义
 */
export function getExamQuestionTypeDefinition(
  examId: string,
  typeId: string
): ExamSpecificQuestionTypeDefinition | undefined {
  return examTypeRegistry.get(examId)?.get(typeId);
}

/**
 * 获取某考试的所有题目类型定义
 */
export function getExamQuestionTypes(examId: string): ExamSpecificQuestionTypeDefinition[] {
  const registry = examTypeRegistry.get(examId);
  return registry ? Array.from(registry.values()) : [];
}

/**
 * 获取基础题目类型的考试特定版本
 */
export function getQuestionTypeForExam(
  examId: string,
  baseType: QuestionType
): ExamSpecificQuestionTypeDefinition | undefined {
  const types = getExamQuestionTypes(examId);
  return types.find((t) => t.baseType === baseType);
}

/**
 * 获取考试特定的题目类型定义（通过基础类型）
 * 返回该考试对此基础类型的所有自定义定义
 */
export function getExamQuestionTypesByBaseType(
  examId: string,
  baseType: QuestionType
): ExamSpecificQuestionTypeDefinition[] {
  const types = getExamQuestionTypes(examId);
  return types.filter((t) => t.baseType === baseType);
}

/**
 * 检查考试是否有特定的题目类型定义
 */
export function hasExamQuestionTypeDefinition(examId: string, typeId: string): boolean {
  return examTypeRegistry.get(examId)?.has(typeId) ?? false;
}

/**
 * 检查考试是否为基础类型定义了考试特定版本
 */
export function hasExamSpecificType(examId: string, baseType: QuestionType): boolean {
  return getQuestionTypeForExam(examId, baseType) !== undefined;
}

/**
 * 移除考试特定的题目类型定义
 */
export function unregisterExamQuestionType(examId: string, typeId: string): boolean {
  return examTypeRegistry.get(examId)?.delete(typeId) ?? false;
}

/**
 * 清除某考试的所有题目类型定义
 */
export function clearExamQuestionTypes(examId: string): void {
  examTypeRegistry.delete(examId);
}

/**
 * 获取所有注册了考试特定类型的考试ID
 */
export function getRegisteredExamIds(): string[] {
  return Array.from(examTypeRegistry.keys());
}

/**
 * 从考试配置注册所有题目类型
 * 便捷函数，用于在考试配置加载时批量注册
 */
export function registerQuestionTypesFromExamConfig(
  examId: string,
  definitions?: Record<string, ExamSpecificQuestionTypeDefinition>
): void {
  if (!definitions) return;

  for (const [id, definition] of Object.entries(definitions)) {
    // 确保定义中的id与键一致
    const defWithId = { ...definition, id };
    registerExamQuestionType(examId, defWithId);
  }
}

/**
 * 获取基础题目类型或考试特定类型
 * 如果考试有特定定义，返回特定定义；否则返回基础定义
 */
export function getEffectiveQuestionType(
  baseType: QuestionType,
  examId?: string
): QuestionTypeMetadata | ExamSpecificQuestionTypeDefinition | undefined {
  if (examId) {
    const examSpecific = getQuestionTypeForExam(examId, baseType);
    if (examSpecific) {
      return examSpecific;
    }
  }
  return getQuestionTypeMetadata(baseType);
}
